import { useState, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import ShippingAddress from "@components/steps/ShippingAddress";
import { useGetShipstationQuotes } from "@queries/useGetShipStationQuotes";
import { calculateCartWeight } from "@utils/priceUtils";
import { SHIPPING_PROMO_SKUS } from "@constants/prices";

export const useShippingCalculation = (
  currentStepComponent: React.ComponentType<any> | null,
  formValues: any,
  setHasAttemptedValidation: (value: boolean) => void
) => {
  const methods = useFormContext();
  const [shippingError, setShippingError] = useState<string | null>(null);

  const products = formValues.items;
  const accessoryProducts = formValues.accessoryProducts;

  // Calculate total shipping weight including both physical products and accessories
  const totalShippingWeight = calculateCartWeight({
    items: products || [],
    accessoryProducts: accessoryProducts || [],
    country: formValues.country,
  });

  // Memoize shipping quotes parameters to prevent unnecessary re-renders
  const shippingQuotesParams = useMemo(() => {
    // Only watch shipping-related fields when on shipping address step
    if (currentStepComponent !== ShippingAddress) {
      return {
        toCountry: "",
        toPostalCode: "",
        toState: "",
        toCity: "",
        weight: { units: "lb", value: totalShippingWeight },
        email: "",
        firstName: "",
        lastName: "",
        products,
        currency: 'usd' as const,
      };
    }

    return {
      toCountry: formValues.country,
      toPostalCode: formValues.postalCode,
      toState: formValues.state,
      toCity: formValues.city,
      weight: { units: "lb", value: totalShippingWeight },
      email: formValues.email,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      products,
      currency: formValues.selectedCurrency || 'usd',
    };
  }, [
    currentStepComponent,
    formValues.country,
    formValues.postalCode,
    formValues.state,
    formValues.city,
    totalShippingWeight,
    formValues.email,
    formValues.firstName,
    formValues.lastName,
    products,
    formValues.selectedCurrency,
  ]);

  const { refetch: fetchShipping, isFetching: isFetchingShipping } = useGetShipstationQuotes(
    currentStepComponent === ShippingAddress
      ? shippingQuotesParams
      : {
          toCountry: "",
          toPostalCode: "",
          toState: "",
          toCity: "",
          weight: { units: "lb", value: 0 },
          email: "",
          firstName: "",
          lastName: "",
          products: [],
          currency: 'usd',
        }
  );

  // Memoize shipping fields completion check to prevent unnecessary re-computations
  const areShippingFieldsComplete = useMemo(() => {
    return !!(
      formValues.address1 &&
      formValues.city &&
      formValues.state &&
      formValues.country &&
      formValues.postalCode
    );
  }, [
    formValues.address1,
    formValues.city,
    formValues.state,
    formValues.country,
    formValues.postalCode,
  ]);

  const handleCalculateShipping = async () => {
    setHasAttemptedValidation(true);
    setShippingError(null); // Clear any previous errors
    methods.setValue("shippingOptions", []); // Clear previous shipping options
    methods.setValue("shipmentRate", null); // Clear previously selected shipping option

    // Validate required shipping fields before making API call
    const requiredFields = ["address1", "city", "state", "country", "postalCode"];
    const isValid = await methods.trigger(requiredFields);

    if (!isValid) {
      // Trigger validation for all fields to show errors
      await methods.trigger();
      return;
    }

    // Check for USA-only free shipping
    const isUS = formValues.country === 'US';
    const hasPhysicalPromoSku = products?.some((item: any) =>
      SHIPPING_PROMO_SKUS.includes(item.sku)
    );
    const hasAccessories = accessoryProducts && Object.keys(accessoryProducts).length > 0;
    const hasFocusStart = products?.some((item: any) => item.sku === 'FOCUS-STRT');

    // USA + Physical Promo + No Accessories + No FOCUS-STRT = Free Shipping
    if (isUS && hasPhysicalPromoSku && !hasAccessories && !hasFocusStart) {
      // Inject free shipping option directly
      const freeShippingOption = {
        serviceName: "Free Shipping",
        serviceCode: "fedex_home_delivery",
        totalCost: 0,
        currency: formValues.selectedCurrency || 'usd',
        signature: "",
      };

      methods.setValue("shippingOptions", [freeShippingOption]);
      methods.setValue("shipmentRate", freeShippingOption); // Auto-select free shipping
      methods.clearErrors("shipmentRate");
      setShippingError(null);
      return; // Skip ShipStation API call
    }

    // For all other cases, calculate shipping normally via ShipStation
    try {
      const res = await fetchShipping();
      if (res.data?.data?.length) {
        methods.setValue("shippingOptions", res?.data?.data);
        // Clear validation errors since we're now in a valid state with shipping options
        methods.clearErrors("shipmentRate");
        setShippingError(null);
      } else {
        setShippingError("Unable to get shipping options for this address");
        methods.setValue("shippingOptions", []);
      }
    } catch (error) {
      setShippingError("Unable to get shipping options for this address");
      methods.setValue("shippingOptions", []);
    }
  };

  return {
    shippingError,
    setShippingError,
    totalShippingWeight,
    areShippingFieldsComplete,
    handleCalculateShipping,
    isFetchingShipping,
  };
};